import type { ContractDocument, NegotiationView } from "@closer/shared";
import {
  answeredText,
  attributionLabel,
  findParentNegotiation,
  NEGOTIATION_STATUS_NAMES,
  negotiationItemStatusName,
} from "@closer/shared";
import { Badge, Button, Card, FormField, Modal, Skeleton, Textarea, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { useIdempotencyKey } from "../../lib/useIdempotencyKey";

/**
 * DIŞ MÜZAKERE (CLOS-252 `[145]`, ADR-0034).
 *
 * ### ⚠ Öneri sözleşmeyi DEĞİŞTİRMİYOR
 *
 * Karşı tarafın önerisi ayrı bir kayıt; metne ancak kullanıcı "Metne uygula" dediğinde
 * giriyor ve o da revizyon yolundan geçiyor. Sessiz birleştirme yok — kartın en sert
 * kuralı.
 *
 * ### ⚠ "Baz eskimiş" ve "hedef değişmiş" TÜRETİLMİŞ
 *
 * Sunucu bu iki bayrağı hesaplayarak veriyor; ekran onları uyarı olarak gösteriyor.
 * Yazılı bir durum olsaydı, sözleşme sonradan değiştiğinde bayrak bayatlardı.
 *
 * ### ⚠ Karar YÖNÜ sunucudan
 *
 * Bir seti karara bağlayacak taraf `decidingTenantId`; öneriyi yapan kendi önerisini
 * kabul edemiyor. Ekran karar düğmelerini yalnız karar sırası bizdeyken açıyor.
 *
 * ### ⚠ YÖNETİMSEL İPTAL ≠ GERİ ÇEKME (CLOS-1117 · yetim uç A)
 *
 * `POST …/negotiations/:negId/cancel` sunucuda vardı, hiçbir ekran onu çağırmıyordu.
 * "Geri çek" ÖNEREN tarafın kendi kararı; "İptal et" SÖZLEŞME SAHİBİNİN akış yönetimi
 * kararı — sunucu `ctx.tenantId !== set.tenantId` olduğunda 409 veriyor. İkisi aynı sette
 * asla birlikte görünmez.
 *
 * ⚠ SAHİPLİK KOŞULU ÖNERENİN TERSİNDEN OKUNUYOR: `NegotiationView` setin SAHİP kiracısını
 * (`set.tenantId`) taşımıyor, yalnız `proposerTenantId`'yi taşıyor. Bu bölüm sahip
 * yüzeyinde çiziliyor, dolayısıyla "öneren biz değilsek iptal bizim işimiz" doğru
 * okumadır; asıl kapı yine sunucuda (ekran kendi kuralını icat etmiyor).
 *
 * ⚠ GEREKÇE ZORUNLU: `config/workflowCancelReassign.json` → negotiation
 * `requireReasonForCancel: true`, `reasonMinLength: 10`. Ekran bu sınırı kullanıcıyı
 * boşuna reddettirmemek için önden gösteriyor; kararı `applyReasonPolicy` veriyor.
 */
export function ContractNegotiationSection({ contractId }: { contractId: string }) {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();

  const anahtar = tenantKey(tenantId, "contract", contractId, "muzakereler");
  const muzakereler = useQuery({
    enabled: can("contract.view"),
    queryKey: anahtar,
    queryFn: async () =>
      (await api<NegotiationView[]>(`/api/contracts/${contractId}/negotiations`, { tenantId }))
        .data,
  });

  const yenile = () => queryClient.invalidateQueries({ queryKey: anahtar });

  /**
   * BAZ VERSİYON BELGELERİ — madde adlarının kaynağı (CLOS-873 kalem-2b).
   *
   * ⚠ TEK SORGU, ÇOK BELGE: her set için ayrı bir hook açamayız (hook'lar döngüye
   * giremez) ve açsaydık set sayısı kadar yeniden çizim olurdu. Benzersiz baz versiyon
   * kimlikleri anahtar oluyor, belgeler paralel çekilip bir haritaya konuyor.
   *
   * ⚠ HATA DALI SESSİZ: belge çekilemezse harita boş kalır ve madde adı "bulunamadı"
   * cümlesine düşer — ham kimliğe DEĞİL. Bölümün kendisi ayakta kalıyor; müzakere
   * listesi bir başlık çözülemedi diye kaybolmamalı.
   */
  const bazKimlikleri = [...new Set((muzakereler.data ?? []).map((s) => s.baseVersionId))].sort();
  const bazBelgeler = useQuery({
    enabled: can("contract.view") && bazKimlikleri.length > 0,
    queryKey: tenantKey(tenantId, "contract", contractId, "muzakere-bazlari", ...bazKimlikleri),
    queryFn: async () => {
      const harita = new Map<string, ContractDocument | null>();
      await Promise.all(
        bazKimlikleri.map(async (versionId) => {
          try {
            const yanit = await api<{ document: ContractDocument | null }>(
              `/api/contracts/${contractId}/versions/${versionId}/document`,
              { tenantId },
            );
            harita.set(versionId, yanit.data.document);
          } catch {
            harita.set(versionId, null);
          }
        }),
      );
      return harita;
    },
  });

  const karsiOneri = useMutation({
    mutationFn: async (girdi: {
      negId: string;
      targetClauseId: string;
      currentText: string;
      proposedText: string;
    }) =>
      api(`/api/contracts/${contractId}/negotiations/${girdi.negId}/counter`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({
          items: [
            {
              targetClauseId: girdi.targetClauseId,
              currentText: girdi.currentText,
              proposedText: girdi.proposedText,
            },
          ],
        }),
      }),
    onSuccess: async () => {
      await yenile();
      toast.success("Karşı öneri gönderildi");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Karşı öneri gönderilemedi"),
  });

  const geriCek = useMutation({
    mutationFn: async (negId: string) =>
      api(`/api/contracts/${contractId}/negotiations/${negId}/withdraw`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({}),
      }),
    onSuccess: async () => {
      await yenile();
      toast.success("Öneri geri çekildi");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Öneri geri çekilemedi"),
  });

  const [iptalHedefi, setIptalHedefi] = useState<string | null>(null);
  /** ⚠ Anahtar NİYETE bağlı: çift tıklama aynı anahtarı taşır (BUG-004). */
  const idempotency = useIdempotencyKey();
  const iptal = useMutation({
    mutationFn: async (girdi: { negId: string; reason: string }) =>
      api(`/api/contracts/${contractId}/negotiations/${girdi.negId}/cancel`, {
        method: "POST",
        tenantId,
        /** ⚠ Uç `idempotency()` zinciriyle korunuyor: aynı iptal iki kez yazılmasın. */
        headers: { "idempotency-key": idempotency.uret() },
        body: JSON.stringify({ reason: girdi.reason }),
      }),
    onSuccess: async () => {
      /** ⚠ Başarıdan sonra YENİ niyet yeni anahtar alıyor; yoksa kullanıcının bilinçli
          ikinci işlemi replay sayılıp sessizce ilk yanıta düşerdi (BUG-004). */
      idempotency.yenile();
      setIptalHedefi(null);
      await yenile();
      toast.success("Müzakere iptal edildi");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Müzakere iptal edilemedi"),
  });

  const karar = useMutation({
    mutationFn: async (girdi: { negId: string; itemId: string; decision: "accept" | "reject" }) =>
      api(`/api/contracts/${contractId}/negotiations/${girdi.negId}/items/${girdi.itemId}/decide`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({ decision: girdi.decision }),
      }),
    onSuccess: async () => {
      await yenile();
      toast.success("Öneri kararı kaydedildi");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Karar kaydedilemedi"),
  });

  const uygula = useMutation({
    mutationFn: async (girdi: { negId: string; itemId: string }) =>
      api(`/api/contracts/${contractId}/negotiations/${girdi.negId}/items/${girdi.itemId}/apply`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({}),
      }),
    onSuccess: async () => {
      await yenile();
      toast.success("Öneri metne uygulandı");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Öneri uygulanamadı"),
  });

  if (!can("contract.view")) return null;

  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <h2 className="mr-auto text-base font-semibold text-fg-heading">Karşı taraf önerileri</h2>
      </div>

      {muzakereler.isPending ? (
        <Skeleton lines={3} />
      ) : muzakereler.error ? (
        <StateView
          state={resolveErrorState(muzakereler.error, {
            idempotent: true,
            overrides: {
              error: { title: "Öneriler yüklenemedi", description: "Bölüm şu an getirilemedi." },
            },
          })}
          onRetry={() => void muzakereler.refetch()}
        />
      ) : (muzakereler.data ?? []).length === 0 ? (
        <p className="text-sm text-fg-muted">
          Karşı taraftan gelen ya da karşı tarafa gönderilen bir değişiklik önerisi yok.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {(muzakereler.data ?? []).map((set) => {
            /** ⚠ Karar sırası bizde mi: sunucunun türettiği yön. */
            const kararBizde = set.decidingTenantId === tenantId && set.status === "open";
            return (
              <div
                key={set.id}
                className="flex flex-col gap-2 rounded-md border border-border-default p-3"
              >
                <div className="flex flex-wrap items-center gap-2">
                  {/*
                    ⚠ Metin ORTAK SÖZLÜKTEN (CLOS-873). Aynı enum artık katılımcı
                    tarafında da çiziliyor; burada kendi metnimizi tutsaydık iki ekran
                    gün gelip ayrışırdı. Sözlük ayrıca TAM KAPSAMALI bir `Record`:
                    buradaki zincirin son dalı (`: "Sonuçlandı"`) enum'a yeni bir değer
                    eklendiğinde onu sessizce "Sonuçlandı" gösterirdi; `Record` aynı
                    durumda derlenmiyor.
                  */}
                  <Badge tone={set.status === "open" ? "warning" : "neutral"}>
                    {NEGOTIATION_STATUS_NAMES[set.status]}
                  </Badge>
                  {/* "SAN Otomotiv A.Ş. adına Ayşe Demir" — kim, kimin adına (CLOS-1152). */}
                  <span className="text-xs text-fg-soft">
                    {attributionLabel({
                      actorName: set.proposerName,
                      onBehalfOf: set.actingPartyName,
                    })}{" "}
                    · {new Date(set.createdAt).toLocaleDateString("tr-TR")}
                  </span>
                  {/* ⚠ Bayat baz TÜRETİLMİŞ: öneri sözleşmenin güncel sürümüne değil. */}
                  {set.staleBase ? (
                    <Badge tone="warning">Öneri eski bir sürüme dayanıyor</Badge>
                  ) : null}
                  {/*
                    ⚠ GERİ ÇEKME YALNIZ KENDİ SETİMİZDE: sunucu da aynısını söylüyor
                    ("Yalnız öneren taraf setini geri çekebilir"). Ekran o kuralı
                    tekrarlıyor, kendi kuralını icat etmiyor; kapı yine sunucuda.

                    ⚠ Uç `contract.revision` istiyor, ekran da onu soruyor (CLOS-888
                    dersi: ekran ucun anahtarını sorar).
                  */}
                  {set.proposerTenantId === tenantId &&
                  ["open", "partially_decided"].includes(set.status) &&
                  can("contract.revision") ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      loading={geriCek.isPending}
                      onClick={() => geriCek.mutate(set.id)}
                    >
                      Geri çek
                    </Button>
                  ) : null}
                  {/*
                    ⚠ İPTAL "GERİ ÇEK"İN TERSİ (CLOS-1117 · yetim uç A): öneren KARŞI
                    taraftaysa set bizim sözleşmemize ait demektir ve yönetimsel iptal
                    sahibin kararıdır. Aynı `contract.revision` anahtarı — uç da onu
                    istiyor.
                  */}
                  {set.proposerTenantId !== tenantId &&
                  ["open", "partially_decided"].includes(set.status) &&
                  can("contract.revision") ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      data-testid="muzakere-iptal-ac"
                      onClick={() => setIptalHedefi(set.id)}
                    >
                      İptal et
                    </Button>
                  ) : null}
                </div>

                {/*
                  ⚠ ZİNCİR BAĞI (CLOS-252 Tren-2C). Parent sunucuda hep vardı ama ekranda
                  görünmüyordu: karşı öneri, cevapladığı öneriyle yan yana duran KARDEŞ bir
                  kart gibi okunuyordu (canlı turda görüldü). Bağ yoksa kullanıcı zinciri
                  değil bağımsız öneriler yığınını görüyor.
                */}
                {(() => {
                  const parent = findParentNegotiation(muzakereler.data ?? [], set);
                  return parent ? (
                    <p className="text-xs text-fg-soft">
                      ↳ {parent.proposerName} önerisine karşılık
                    </p>
                  ) : null;
                })()}

                {/*
                  ⚠ SONUÇLANMIŞ SET KAPANMIŞ OKUNUYOR: aksiyonların yokluğu tek başına
                  "asılı kalmış" gibi görünüyordu. Sebep AÇIKÇA yazılıyor.
                */}
                {set.status === "countered" ? (
                  <p className="text-xs text-fg-soft">Bu öneri karşı öneriyle kapandı</p>
                ) : null}
                {set.status === "withdrawn" ? (
                  <p className="text-xs text-fg-soft">Öneren taraf bu öneriyi geri çekti</p>
                ) : null}

                {set.note ? <p className="text-sm text-fg">{set.note}</p> : null}

                <ul className="flex flex-col gap-2">
                  {set.items.map((oge) => (
                    <li key={oge.id} className="flex flex-col gap-1 rounded-md bg-muted p-2">
                      <span className="text-xs text-fg-soft">
                        {maddeAdi(
                          bazBelgeler.data?.get(set.baseVersionId) ?? null,
                          oge.targetClauseId,
                          bazBelgeler.isPending,
                        )}
                        {/*
                          ⚠ METİN DÜZELTİLDİ (CLOS-873 dönüşü). Eskiden *"hedef metin bu
                          öneriden sonra değişti"* yazıyordu ve bu YANILTICIYDI: sunucu
                          bayrağı `contract_contents` üzerinden, yani ÇALIŞMA TASLAĞINDAN
                          türetiyor. Öneri yayımlanmış bir SÜRÜME dayandığı için taslak
                          zaten farklı olabiliyor — kimse bir şey değiştirmemişken de
                          bayrak yanıyordu. Yeni metin bayrağın gerçekte söylediği şeyi
                          söylüyor ve taslağı ADIYLA anıyor.

                          ⚠ Bayrak YALNIZ BURADA çiziliyor: taslağı yalnız sahip görüyor.
                          Katılımcı yüzeyinde bilerek yok (`SharedNegotiationSection`).
                        */}
                        {oge.targetChanged
                          ? " · bu madde güncel çalışma metninde, önerinin hedeflediği metinden farklı"
                          : ""}
                      </span>
                      {/*
                        ⚠ ÜSTÜ ÇİZİLİ METİN CEVAPLANAN ŞEY (CLOS-252 Tren-2C).

                        Eskiden öğenin `currentText`'i, yani BAZ VERSİYON metni yazıyordu.
                        Zincir "iki yıl → bir yıl → on sekiz ay" iken ekran "iki yıl → on
                        sekiz ay" gösteriyor, cevaplanan öneri kayboluyordu.

                        ⚠ ETİKET DE TÜRETMEDEN GELİYOR: parent bulunamazsa baz metne
                        düşülüyor ve etiket "Mevcut metin" oluyor — "cevaplanan öneri
                        buydu" demek yalan olurdu.
                      */}
                      {(() => {
                        const taban = answeredText(muzakereler.data ?? [], set, oge);
                        return (
                          <>
                            <span className="text-xs text-fg-soft">
                              {taban.kind === "parent" ? "Cevaplanan öneri" : "Mevcut metin"}
                            </span>
                            <span className="text-xs text-fg-muted line-through">{taban.text}</span>
                          </>
                        );
                      })()}
                      <span className="text-sm text-fg">{oge.proposedText}</span>
                      {oge.note ? <span className="text-xs text-fg-soft">{oge.note}</span> : null}

                      <div className="flex flex-wrap items-center gap-2">
                        {/*
                          ⚠ ROZET SET DURUMUNDAN TÜRÜYOR (CLOS-252 Tren-2C): set "Geri
                          çekildi" iken karara bağlanmamış öğe "Bekliyor" yazıyordu —
                          kullanıcıya var olmayan bir iş gösteriyordu. Verilmiş karar
                          ezilmiyor: türetme yalnız `pending` öğeye uygulanıyor.
                        */}
                        <Badge tone={oge.decision === "accepted" ? "success" : "neutral"}>
                          {negotiationItemStatusName(set.status, oge.decision)}
                        </Badge>
                        {kararBizde && oge.decision === "pending" ? (
                          <>
                            <Button
                              type="button"
                              size="sm"
                              onClick={() =>
                                karar.mutate({ negId: set.id, itemId: oge.id, decision: "accept" })
                              }
                            >
                              Kabul et
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                karar.mutate({ negId: set.id, itemId: oge.id, decision: "reject" })
                              }
                            >
                              Reddet
                            </Button>
                          </>
                        ) : null}
                        {/* ⚠ Uygulama AYRI adım: kabul metni değiştirmiyor. */}
                        {oge.decision === "accepted" &&
                        oge.appliedVersionId === null &&
                        can("contract.revision") ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={() => uygula.mutate({ negId: set.id, itemId: oge.id })}
                            loading={uygula.isPending}
                          >
                            Metne uygula
                          </Button>
                        ) : null}
                        {oge.appliedVersionId ? (
                          <span className="text-xs text-fg-soft">Metne uygulandı</span>
                        ) : null}
                      </div>

                      {/*
                        ⚠ KARŞI ÖNERİ ZİNCİRİ İKİ YÖNLÜ YAPAN YÜZEY (CLOS-252 Tren-2A).

                        Uç sahip yüzeyinde VARDI ama hiçbir ekran onu çağırmıyordu:
                        sahip kabul/ret verebiliyor, karşı öneri veremiyordu. Koşullar
                        sunucunun kendi kurallarının aynısı — karar sırası bizde, set
                        hâlâ cevaplanabilir durumda ve öğe henüz karara bağlanmamış.
                      */}
                      {kararBizde && oge.decision === "pending" && can("contract.revision") ? (
                        <KarsiOneriFormu
                          baslik={maddeAdi(
                            bazBelgeler.data?.get(set.baseVersionId) ?? null,
                            oge.targetClauseId,
                            bazBelgeler.isPending,
                          )}
                          bekliyor={karsiOneri.isPending}
                          onGonder={(proposedText) =>
                            karsiOneri.mutate({
                              negId: set.id,
                              targetClauseId: oge.targetClauseId,
                              currentText: oge.currentText,
                              proposedText,
                            })
                          }
                        />
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      <MuzakereIptalDialog
        open={iptalHedefi !== null}
        pending={iptal.isPending}
        onClose={() => setIptalHedefi(null)}
        onConfirm={(reason) => iptalHedefi && iptal.mutate({ negId: iptalHedefi, reason })}
      />
    </Card>
  );
}

/** Sunucudaki `reasonMinLength` (config/workflowCancelReassign.json). */
const IPTAL_GEREKCE_ASGARI = 10;

/**
 * MÜZAKERE YÖNETİMSEL İPTAL DİYALOĞU (CLOS-1117 · yetim uç A).
 *
 * ### ⚠ Neden ortak `WorkflowCancelDialog` DEĞİL
 *
 * O bileşen AKIŞ seviyesindeki gövdesiz `.../cancel` uçları için yazıldı ve gerekçe alanı
 * BİLEREK yok (kendi doc yorumu bunu yazıyor). Bu uç gövde alıyor ve gerekçe ZORUNLU;
 * ortak bileşene isteğe bağlı bir alan eklemek dört çağıranın hepsinin sözleşmesini
 * gevşetirdi. Tek çağıranı olan yerel diyalog daha dar bir söz veriyor.
 *
 * ### ⚠ İptal GERİ ALINAMAZ
 *
 * Set `cancelled` olduktan sonra öğelerine karar verilemiyor; müzakere ancak yeni bir
 * öneriyle sürer. Bu yüzden tek tık değil, gerekçeli bir onay adımı var.
 */
function MuzakereIptalDialog({
  open,
  pending,
  onClose,
  onConfirm,
}: {
  open: boolean;
  pending: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [gerekce, setGerekce] = useState("");

  // Her açılış temiz başlar: önceki denemenin gerekçesi başka bir sete taşınmaz.
  useEffect(() => {
    if (open) setGerekce("");
  }, [open]);

  const kirpilmis = gerekce.trim();
  const eksik = kirpilmis.length < IPTAL_GEREKCE_ASGARI;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Müzakereyi iptal et"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            type="button"
            variant="danger"
            loading={pending}
            disabled={eksik || pending}
            data-testid="muzakere-iptal-onayla"
            onClick={() => onConfirm(kirpilmis)}
          >
            İptal et
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-fg-muted">
          Set kapanır ve öğelerine artık karar verilemez. Müzakere ancak yeni bir öneriyle
          sürebilir.
        </p>
        <FormField
          label="Gerekçe"
          error={
            eksik && kirpilmis.length > 0
              ? `Gerekçe en az ${IPTAL_GEREKCE_ASGARI} karakter olmalıdır`
              : undefined
          }
        >
          <Textarea
            id="muzakere-iptal-gerekce"
            value={gerekce}
            onChange={(olay) => setGerekce(olay.target.value)}
            rows={3}
            placeholder="Müzakerenin neden iptal edildiğini yazın."
          />
        </FormField>
      </div>
    </Modal>
  );
}

/**
 * MADDE ADI — SAHİP TARAFI (CLOS-873 kalem-2b).
 *
 * ### ⚠ HAM KİMLİK EKRANA ÇIKMAZ
 *
 * Ekran eskiden `Madde {targetClauseId}` yazıyordu, yani kullanıcıya bir UUID. Katılımcı
 * tarafında bu CLOS-873'te çözülmüştü (`maddeAdi()`); sahip tarafı ham kimlikte kalmıştı.
 *
 * ### ⚠ HER DAL KENDİ CÜMLESİNİ SÖYLER
 *
 * Çözülemeyen dallar ham kimliğe DÜŞMÜYOR: düşselerdi "çözüldü" ile "çözülemedi" ayırt
 * edilemez, kullanıcı da hangi maddeye baktığını sanıp yanılırdı. Üç ayrı cümle var ve
 * üçü de testli.
 */
function maddeAdi(belge: ContractDocument | null, clauseId: string, yukleniyor: boolean): string {
  if (yukleniyor) return "Madde yükleniyor…";
  const madde = belge?.clauses?.find((c) => c.id === clauseId);
  if (!madde) return "Madde bu sürümde bulunamadı";
  return madde.title.trim().length > 0 ? `Madde: ${madde.title}` : "Başlıksız madde";
}

/**
 * SAHİP TARAFI KARŞI ÖNERİ FORMU (CLOS-252 Tren-2A).
 *
 * ### ⚠ KARŞI ÖNERİ SETİN ÖĞESİNE VERİLİYOR
 *
 * Katılımcı tarafındaki form bir madde SEÇTİRİYOR çünkü orada yeni bir set açmak da aynı
 * formdan geçiyor. Sahip tarafı yalnız CEVAP veriyor: karşı tarafın itiraz ettiği maddeye
 * kendi metnini öneriyor. Madde seçici koymak, cevaplanan şeyi bulanıklaştırırdı.
 *
 * ⚠ SINIR YAZILI: setteki maddeler DIŞINDA bir maddeye karşı öneri vermek daha geniş bir
 * düzenleyici ve kendi kartını hak ediyor. "Eksik" değil "dar" olduğu görünsün diye burada.
 *
 * ### ⚠ MEVCUT METİN ÖĞEDEN, KULLANICIDAN DEĞİL
 *
 * `currentText` öğenin taşıdığı metin — yani baz versiyondaki hâli. Kullanıcıya
 * yazdırsaydık sunucunun "hedef metin değişti" türetmesinin bir tarafı uydurma olurdu.
 */
function KarsiOneriFormu({
  baslik,
  bekliyor,
  onGonder,
}: {
  baslik: string;
  bekliyor: boolean;
  onGonder: (proposedText: string) => void;
}) {
  const [acik, setAcik] = useState(false);
  const [metin, setMetin] = useState("");

  if (!acik) {
    return (
      <Button
        type="button"
        size="sm"
        variant="secondary"
        data-testid="sahip-karsi-oneri-ac"
        onClick={() => setAcik(true)}
      >
        Karşı öneri ver
      </Button>
    );
  }

  return (
    <div className="flex w-full flex-col gap-2" data-testid="sahip-karsi-oneri">
      <FormField label={`${baslik} için önerdiğiniz metin`}>
        <Textarea
          id="sahip-karsi-oneri-metin"
          value={metin}
          onChange={(olay) => setMetin(olay.target.value)}
          placeholder="Örn. Teslim 45 gün içinde yapılır."
        />
      </FormField>
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          loading={bekliyor}
          disabled={metin.trim() === ""}
          onClick={() => onGonder(metin.trim())}
        >
          Karşı öneri gönder
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => setAcik(false)}>
          Vazgeç
        </Button>
      </div>
    </div>
  );
}
